// frontend/src/utils/formatMeasure.ts
//
// Display formatter for measured quantities on spray/nutrition instructions:
// cap decimals at 2 and normalise to the largest unit in the value's family
// whose magnitude is >= 1 (0.5 L -> 500 ml, 1500 ml -> 1.5 L, 4800 g -> 4.8 kg).
//
// Mirror of backend/src/utils/formatMeasure.js — keep the two in sync.
// This is the default display formatter for measured quantities across the
// Protection/Nutrition/Consumables surfaces — instructions, session
// consumption tables, and the stock-management ledger/level tables (Stock
// Movements, Stock Levels). NOTE: formatQuantity.ts (fixed-2dp, no rescale) is
// retained only where a displayed value must stay in the same unit as an
// adjacent entry field — the stock-count page's system-stock column.

import { UNIT_FAMILIES, UNITS_BY_FAMILY, canonicalUnit } from './units';

const EM_DASH = '—';

function formatNumber(value: number): string {
  const rounded = Math.sign(value) * Math.round((Math.abs(value) + 1e-10) * 100) / 100;
  if (rounded === 0) {
    if (value === 0) return '0';
    return value > 0 ? '<0.01' : '>-0.01';
  }
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(rounded);
}

export function formatMeasure(
  value: number | null | undefined,
  unit?: string | null,
): string {
  if (value == null || !Number.isFinite(value)) return EM_DASH;

  const canon = canonicalUnit(unit); // { unit, family } | null
  if (!canon) {
    return unit ? `${formatNumber(value)} ${unit}` : formatNumber(value);
  }

  if (value === 0) return `0 ${canon.unit}`;

  const factors = UNIT_FAMILIES[canon.family];
  const base = value * factors[canon.unit];
  const family = UNITS_BY_FAMILY[canon.family]; // largest -> smallest
  let chosen = family[family.length - 1];        // smallest as fallback
  for (const u of family) {
    if (Math.abs(base / factors[u]) >= 1) { chosen = u; break; }
  }
  const display = base / factors[chosen];
  return `${formatNumber(display)} ${chosen}`;
}
